import {
  LoginRequest,
  SignupRequest,
  ForgetPasswordRequest,
  PasswordRequest,
  SetupAccountRequest,
  ChangePasswordRequest,
  SSOLoginRequest,
  SSOCallbackRequest,
  SSOLogoutRequest
} from "models/user";
import { RiskStates } from "constants/index";
import { message } from "antd";
import AuthUtils from "utils/auth-utils";
import axios from "axios";
import { client, noAuthClient } from "./axios";
import {
  BASE_URL,
  BASE_URL_CONFIG,
  BASE_URL_WITHOUT_GATEWAY
} from "./endpoints";
import CIQClient from "./ciq-client";

export const {
  getIDToken,
  getRefreshToken,
  getAccessToken,
  getIsSSOLogin,
  getUser,
  logout,
  setIDToken,
  setAccessToken,
  setRefreshToken,
  setIsSSOLogin,
  getAssociatedSubscriptions,
  setAssociatedSubscriptions,
  getCurrentSubscriptionId,
  setCurrentSubscriptionId,
  setAccessTokenAndSubscriptionId,
  getLoggedinUserId,
  getSubscriptionId,
  isTokenExpired
} = AuthUtils;

export const login = async (request: LoginRequest) => {
  try {
    const response = await noAuthClient.post(`${BASE_URL}/user/login`, request);
    return response;
  } catch (error) {
    return error;
  }
};

export const ssoLogin = async (request: SSOLoginRequest) => {
  try {
    const response = await noAuthClient.post(`${BASE_URL}/sso/login`, request);
    return response;
  } catch (error) {
    return error;
  }
};

export const ssoCallback = async (request: SSOCallbackRequest) => {
  try {
    const response = await axios.get(
      `${BASE_URL_CONFIG}/callback?code=${request.code}`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const ssoLogout = async (request: SSOLogoutRequest) => {
  try {
    const response = await noAuthClient.post(`${BASE_URL}/logout`, request);
    return response;
  } catch (error) {
    return error;
  }
};

export const signup = async (request: SignupRequest) => {
  try {
    const response = await noAuthClient.post(`${BASE_URL}/user/register`, {
      email: request.email,
      company: request.company,
      subscription: request.subscription
    });
    return response;
  } catch (error) {
    return error;
  }
};

export const setupAccount = async (request: SetupAccountRequest) => {
  const headers = {
    Authorization: `Bearer ${request.token}`
  };
  const body = {
    password: request.password,
    first_name: request.first_name,
    last_name: request.last_name,
    phone: request.phone
  };
  try {
    const response = await noAuthClient.put(
      `${BASE_URL}/user/setupAccount`,
      body,
      {
        headers
      }
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const upadatepassword = async (request: PasswordRequest) => {
  const headers = {
    Authorization: `Bearer ${request.token}`
  };
  const body = {
    password: request.password
  };
  try {
    const response = await noAuthClient.put(`${BASE_URL}/user/password`, body, {
      headers
    });
    return response;
  } catch (error) {
    return error;
  }
};

export const changepassword = async (request: ChangePasswordRequest) => {
  try {
    const response = await client.put(`${BASE_URL}/user/modifyPassword`, {
      old_password: request.old_password,
      new_password: request.new_password
    });
    return response;
  } catch (error) {
    return error;
  }
};

export const changeExpiredPassword = async (request: {
  new_password: string;
  from_expiry: boolean;
}) => {
  try {
    const token = getAccessToken();
    if (!token) {
      return { error: "No token found" };
    }

    const response = await client.put(
      `${BASE_URL}/user/modifyPassword`,
      request,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const forgetpassword = async (request: ForgetPasswordRequest) => {
  const body = { email: request.email };
  try {
    const response = await noAuthClient.post(
      `${BASE_URL}/user/forgotPassword`,
      body
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const getTokenForSubscription = async (request: any) => {
  let response = null;
  if (request.loginInfo) {
    // to optionally pass the config object
    const config = {
      headers: {
        Authorization: `Bearer ${request.loginInfo.token}`
      }
    };
    response = await noAuthClient.post(
      `${BASE_URL}/user/login/subscriptionExchange`,
      { subscription_id: request.subscriptionId },
      config
    );
  } else {
    response = await client.post(
      `${BASE_URL}/user/login/subscriptionExchange`,
      { subscription_id: request.subscriptionId }
    );
  }
  setAccessToken(response.data?.success?.token);
  setAssociatedSubscriptions(response.data?.success?.associated_subscriptions);
  return response;
};

export const getTokenForSubscriptionSSO = async (request: any) => {
  let response = null;

  response = await CIQClient.post({
    url: `${BASE_URL}/sso/exchange-token`,
    data: {
      subscription_id: request.subscriptionId,
      refresh_token: request.refreshToken,
      id_token: request.idToken
    }
  });

  setAccessToken(response.data?.tokens?.AccessToken);
  // setAssociatedSubscriptions(response.data?.success?.associated_subscriptions);
  return response;
};

export const getTokenForProject = async (request: { projectId: string }) => {
  try {
    const response = await client.post(`${BASE_URL}/user/login/tokenExchange`, {
      project_id: request.projectId,
      dynamic_permission: true
    });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const getTokenForProjectSSO = async (request: {
  idToken: string;
  refreshToken: string;
  projectId: string;
}) => {
  try {
    const response = await CIQClient.post({
      url: `${BASE_URL}/sso/exchange-token`,
      data: {
        project_id: request.projectId,
        refresh_token: request.refreshToken,
        id_token: request.idToken
      }
    });
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const getTokenForProjectNoAuth = async (request: {
  projectId: string;
  token: string;
}) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${request.token}`
      }
    };
    const response = await noAuthClient.post(
      `${BASE_URL}/user/login/tokenExchange`,
      {
        project_id: request.projectId,
        dynamic_permission: true
      },
      config
    );
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

export const heartbeatAPI = async () => {
  try {
    const response = await client.post(`${BASE_URL}/user/heartbeat`);
    return { success: true, data: response };
  } catch (error: any) {
    if (error?.response?.status === 401) {
      message.error("Session expired, redirecting to login");
      logout();
    }
    return { success: false, error };
  }
};

export const getDashBoardData = async (token: string, input = {}) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  return CIQClient.post({
    url: `${BASE_URL_WITHOUT_GATEWAY}/report-engine/dashboard`,
    data: input,
    config
  });
};

export const getDashBoardDataV2 = async (token: string, input = {}) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  return CIQClient.post({
    url: `${BASE_URL_WITHOUT_GATEWAY}/report-engine/v2/dashboard`,
    data: input,
    config
  });
};

export const getActivitiesModifiedCount = async (token: string, input = {}) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  return CIQClient.post({
    url: `${BASE_URL}/v2/scheduleImpactActivitiesCount`,
    data: input,
    config
  });
};

export const getScheduleLookupReport = async (token: string, input = {}) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  return CIQClient.post({
    url: `${BASE_URL}/scheduleReport`,
    data: input,
    config
  });
};

type SendEmailInvitedUserType = {
  env_admin: boolean;
  input: {
    email: string;
    subscription_id: string;
  };
};
export const sendEmailToInvitedUserInSubscription = async (
  request: SendEmailInvitedUserType
) => {
  const response = await client.post(
    `${BASE_URL}/user/addToSubscription`,
    request
  );
  return response;
};

export const validateUser = async (
  user_id: string,
  token: string,
  subscription_id?: string,
  project_id?: string
) => {
  const body = { user_id, subscription_id, project_id };
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  try {
    const response = await noAuthClient.post(
      `${BASE_URL}/user/validate`,
      body,
      config
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const getAIMLProjectSummaryData = async (
  token: string,
  projectId: string
) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  return CIQClient.get({
    url: `https://lffkpkbf02.execute-api.us-east-1.amazonaws.com/import-summary/${projectId}`,
    config
  });
};

export const getAIMLMaterialSuggestedActivities = async (
  token: string,
  materialId: string
) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  return CIQClient.get({
    url: `https://lffkpkbf02.execute-api.us-east-1.amazonaws.com/suggest-activities/${materialId}`,
    config
  });
};

export const getProjectListV2 = async (token: string, input = {}) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  return CIQClient.get({
    url: `${BASE_URL_WITHOUT_GATEWAY}/report-engine/projects`,
    data: input,
    config
  });
};

export const getRiskReportSummary = async (token: string) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  return CIQClient.get({
    url: `${BASE_URL_WITHOUT_GATEWAY}/report-engine/v2/reports/risk-summary-report`,
    config
  });
};

export const getSubmittalsReport = async (
  token: string,
  risk_level?: RiskStates,
  submittalIds?: Array<string>
) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  let url = `${BASE_URL_WITHOUT_GATEWAY}/report-engine/v2/reports/submittal-cumulative-risk-report?`;
  if (risk_level) {
    url += `risk_level=${risk_level}`;
  }
  if (submittalIds && submittalIds.length > 0) {
    url += `&feature_instance_ids=${submittalIds.join(
      "&feature_instance_ids="
    )}`;
  }
  return CIQClient.get({
    url,
    config
  });
};

export const getMaterialsReport = async (
  token: string,
  risk_level?: RiskStates,
  materialIds?: Array<string>
) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  let url = `${BASE_URL_WITHOUT_GATEWAY}/report-engine/v2/reports/material-cumulative-risk-report?`;
  if (risk_level) {
    url += `risk_level=${risk_level}`;
  }
  if (materialIds && materialIds.length > 0) {
    url += `&feature_instance_ids=${materialIds.join(
      "&feature_instance_ids="
    )}`;
  }
  return CIQClient.get({
    url,
    config
  });
};
